import React, { useRef, useMemo, useCallback } from "react";
import "./css/DataTable.css";
import { useVirtualizer } from "@tanstack/react-virtual";
import { useTableSort } from "./hooks/useTableSort";
import { useScrollToActiveRow } from "./hooks/useScrollToActiveRow";
import { getScrollElement } from "./utils/scrollUtils";
import { TableRow } from "./components/TableRow";

interface DataTableProps {
	headers: Array<string | React.ReactNode>;
	rawValues: Array<Array<any>>;
	renderedValues: Array<Array<any>>;
	sortableColumns?: boolean[];
	activeRow?: number | null;
	onRowClick?: (rowIndex: number) => void;
}

function VirtualizedDataTable({ headers, rawValues, renderedValues, sortableColumns, activeRow, onRowClick }: DataTableProps) {
	const tableContainerRef = useRef<HTMLDivElement>(null);
	const activeRowRefDiv = useRef<HTMLDivElement>(null);
	const activeRowRefTr = useRef<HTMLTableRowElement>(null);

	// Sorting logic
	const { sortColumn, sortDirection, displayedValues, indexMapping, handleSort, getTargetIndex } = useTableSort({
		rawValues,
		renderedValues,
		sortableColumns,
	});

	// Get scroll element function for virtualizer
	// Finds .events-container which is the scroll container
	const getScrollElementFn = useCallback(() => {
		const element = getScrollElement(tableContainerRef);
		// Fallback: directly find .events-container if traversal didn't find it
		if (!element?.classList.contains("events-container")) {
			const eventsContainer = tableContainerRef.current?.closest(".events-container") as HTMLElement;
			return eventsContainer || element;
		}
		return element;
	}, []);

	// Virtualizer for table rows
	// eslint-disable-next-line react-hooks/incompatible-library
	const rowVirtualizer = useVirtualizer({
		count: displayedValues.length,
		getScrollElement: getScrollElementFn,
		estimateSize: () => 60, // Estimated height of each table row
		overscan: 10, // Render 10 items above and below visible area
	});

	const totalContentHeight = rowVirtualizer.getTotalSize();
	const virtualItems = rowVirtualizer.getVirtualItems();

	// Calculate the target index (mapped index when sorting is active)
	const targetIndex = useMemo(() => getTargetIndex(activeRow ?? null), [getTargetIndex, activeRow]);

	// Scroll to active row
	useScrollToActiveRow({
		targetIndex,
		shouldVirtualize: true,
		rowVirtualizer,
		activeRowRefDiv,
		activeRowRefTr,
		displayedValuesLength: displayedValues.length,
	});

	// Memoize header click handler
	const handleHeaderClick = useCallback(
		(colIndex: number) => () => {
			handleSort(colIndex);
		},
		[handleSort]
	);

	// Memoize row click handler
	const handleRowClick = useCallback(
		(rowIndex: number) => {
			const originalIndex = indexMapping[rowIndex];
			onRowClick?.(originalIndex);
		},
		[indexMapping, onRowClick]
	);

	return (
		<div ref={tableContainerRef} className="data-table__wrapper">
			<table className="data-table">
				<thead className="data-table__header" style={{ position: "sticky", top: 0, zIndex: 10, background: "white" }}>
					<tr>
						{headers.map((header, idx) => {
							const isActionHeader = typeof header !== "string";
							return (
								<th
									key={idx}
									onClick={isActionHeader ? undefined : handleHeaderClick(idx)}
									className={
										(isActionHeader || (sortableColumns && !sortableColumns[idx]) ? "not-sortable" : "sortable") +
										(!isActionHeader && sortColumn === idx && sortDirection ? ` sorted-${sortDirection}` : "") +
										(idx !== 0 ? " th-header" : "") +
										(idx === 0 ? " data-table__header-first" : "") +
										(isActionHeader ? " data-table__header-action" : "")
									}
								>
									{header}
									{!isActionHeader && sortableColumns && sortableColumns[idx] && sortColumn === idx && sortDirection && (
										<span className="sort-indicator">{sortDirection === "asc" ? " ▲" : " ▼"}</span>
									)}
								</th>
							);
						})}
					</tr>
				</thead>
				<tbody>
					<tr>
						<td colSpan={headers.length} style={{ padding: 0, height: totalContentHeight }}>
							<div style={{ position: "relative", height: totalContentHeight }}>
								{virtualItems.map((virtualRow) => {
									const row = displayedValues[virtualRow.index];
									const rowIndex = virtualRow.index;
									const isActive = targetIndex !== null && rowIndex === targetIndex;

									return (
										<TableRow
											key={rowIndex}
											row={row}
											rowIndex={rowIndex}
											isActive={isActive}
											onClick={() => handleRowClick(rowIndex)}
											headers={headers as string[]}
											isVirtualized={true}
											virtualRowStart={virtualRow.start}
											virtualRowSize={virtualRow.size}
											activeRowRefDiv={isActive ? activeRowRefDiv : null}
										/>
									);
								})}
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
	);
}

export default VirtualizedDataTable;
